import { useEffect, useRef, useState } from "react";
import { StopAnswerSheet } from "../components/games/StopAnswerSheet.jsx";
import { StopLetterDraw } from "../components/games/StopLetterDraw.jsx";
import { useRoom } from "../contexts/RoomContext.jsx";
import { SoundButton } from "../contexts/SoundContext.jsx";
import { STOP_CATEGORIES } from "../data/stopCategories.js";
import { respostasStopCompletas } from "../domain/stop.js";
import { useStopAnswers } from "../hooks/useStopAnswers.js";

export function StopMultiplayerPage() {
  const { user, roomData, serverTimeOffset, salvarRespostasStop, pararStop } =
    useRoom();
  const roundTime =
    roomData?.config?.tempoRodada === undefined
      ? 60
      : Number(roomData.config.tempoRodada);
  const letter = roomData?.stopLetraAtual || "";
  const userId = user?.uid;
  const [drawing, setDrawing] = useState(true);
  const [drawDuration, setDrawDuration] = useState(4000);
  const [remaining, setRemaining] = useState(roundTime);
  const stoppedRef = useRef(false);
  const saveTimeout = useRef(null);
  const initialAnswers = roomData?.stopRespostas?.[userId] || {};
  const engine = useStopAnswers({
    active: !drawing,
    initialAnswers,
    resetKey: `${roomData?.rodadaAtual}_${letter}`,
  });

  useEffect(() => {
    stoppedRef.current = false;
    const serverNow = Date.now() + serverTimeOffset;
    const wait = (roomData?.timestampInicioRodada || serverNow) - serverNow;
    if (wait <= 0) setDrawing(false);
    else {
      setDrawDuration(wait);
      setDrawing(true);
    }
  }, [
    roomData?.rodadaAtual,
    roomData?.timestampInicioRodada,
    serverTimeOffset,
  ]);

  useEffect(() => {
    if (drawing || !userId) return undefined;
    window.clearTimeout(saveTimeout.current);
    saveTimeout.current = window.setTimeout(
      () => salvarRespostasStop(userId, engine.answers),
      500,
    );
    return () => window.clearTimeout(saveTimeout.current);
  }, [drawing, engine.answers, userId]);

  const stopRound = async (stoppedBy) => {
    if (stoppedRef.current) return;
    stoppedRef.current = true;
    window.clearTimeout(saveTimeout.current);
    try {
      await salvarRespostasStop(userId, engine.answersRef.current);
      await pararStop(stoppedBy);
    } catch (error) {
      stoppedRef.current = false;
      console.error("Erro ao encerrar a rodada de Stop:", error);
    }
  };

  useEffect(() => {
    if (drawing || roundTime === 0) return undefined;
    const updateTimer = () => {
      const serverNow = Date.now() + serverTimeOffset;
      const elapsed = Math.floor(
        (serverNow - (roomData.timestampInicioRodada || serverNow)) / 1000,
      );
      const next = Math.max(0, roundTime - elapsed);
      setRemaining(next);
      if (next <= 0) stopRound(null);
    };
    updateTimer();
    const interval = window.setInterval(updateTimer, 1000);
    return () => window.clearInterval(interval);
  }, [drawing, roomData?.timestampInicioRodada, roundTime, serverTimeOffset]);

  const complete = respostasStopCompletas(engine.answers, STOP_CATEGORIES);
  const players = Object.values(roomData?.players || {});
  const completedPlayers = players.filter((player) => {
    const answers = roomData?.stopRespostas?.[player.id] || {};
    return respostasStopCompletas(answers, STOP_CATEGORIES);
  }).length;

  return (
    <div className="stop-game-shell">
      <header className="stop-game-header">
        <div>
          <SoundButton variante="inline" />
          <h2>🛑 Stop — Rodada {roomData?.rodadaAtual || 1}</h2>
        </div>
        {!drawing && (
          <strong className={roundTime > 0 && remaining <= 15 ? "urgent" : ""}>
            ⏱️ {roundTime === 0 ? "∞" : `${remaining}s`}
          </strong>
        )}
      </header>
      {drawing ? (
        <StopLetterDraw
          letraFinal={letter}
          duracaoMs={drawDuration}
          aoTerminar={() => setDrawing(false)}
        />
      ) : (
        <StopAnswerSheet
          letter={letter}
          answers={engine.answers}
          activeIndex={engine.activeIndex}
          onActiveIndexChange={engine.setActiveIndex}
          onType={engine.typeKey}
          onStop={() => stopRound(userId)}
          canStop={complete}
          progressLabel={`${completedPlayers}/${players.length} já terminaram`}
        />
      )}
    </div>
  );
}
